// Public demonstration records only. These open WITHOUT a PIN - a prospect should never
// have to work out a demo PIN to see the product (Daniel 2026-08-14). Real customer records
// are served from Firestore behind a PIN-verifying Cloud Function and share none of this.
//
// These are adapted into the new customer-portal allowlist shape by the record view,
// so demos exercise the same visible panels without touching customer data:
//
//   reading.hours / reading.unit    numeric reading + whether it is hours or miles
//   verifiedReading                 what WE recorded at the last service - the figure
//                                   the maintenance program calculates from
//   oilDue.dueAtHours               numeric, so countdowns recompute against a
//                                   customer-entered reading
//   dataSheet.fluids[].capacity     what to pack for a top-off
//   dataSheet.coolantCaution        coolant families must never be cross-mixed
//   greaseStops                     the cadence being kept
//
// Deliberately absent: filter and part numbers. They were removed from the portal
// (noise, and the number is on the filter), so demo records must not show them either.
//
// Day counts are NOT stored. The record view computes days remaining from oilDue.date,
// so these demo records stay accurate as they sit here rather than going stale.
export const previewMaintainedUnits = [
  {
    lookupNumber: '0001',
    publicLabel: 'Mahindra 4540 compact tractor',
    greeting: 'Hey, Jeff!',
    type: 'Example compact tractor',
    make: 'Mahindra',
    model: '4540',
    programStatus: 'Active',
    reading: { label: 'Current hours', value: '642.3 hours', hours: 642.3, unit: 'hours' },
    verifiedReading: { hours: 620.0, date: 'July 28, 2026' },
    lastService: {
      title: '50-hour inspection and chassis service',
      date: 'July 28, 2026',
      reading: '620.0 hours',
      detail: 'Greased all service points, inspected fluid levels, and checked loader pins and bushings.',
    },
    previousRepair: {
      title: 'Left loader lift-cylinder repair',
      date: 'February 19, 2026',
      reading: '488.2 hours',
      detail: 'Repacked the cylinder, inspected the rod, and verified operation under load.',
    },
    upcomingService: {
      due: '700.0 hours',
      dueAtHours: 700,
      remaining: '57.7 hours remaining',
      item: 'Replace the hydraulic/transmission filter and clean the suction screen.',
    },
    checkupSchedule: {
      frequency: 'Monthly checkup schedule',
      nextGreaseDate: 'August 28, 2026',
      basis: 'Scheduled monthly from the July 28 service',
    },
    lastOilChange: { date: 'June 12, 2026', hours: 592.3 },
    oilDue: {
      reading: '742.3 hours',
      dueAtHours: 742.3,
      date: 'October 15, 2026',
      remaining: '100.0 operating hours remaining',
    },
    dataSheet: {
      greaseZerks: '23',
      fluids: [
        {
          system: 'Engine oil',
          specification: 'SAE 15W-40, API CK-4',
          capacity: '8.5 qt',
        },
        {
          system: 'Hydraulic / transmission',
          specification: 'J20C-spec universal tractor fluid',
          dealerSpec: 'Mahindra premium UTF',
          capacity: '10.5 gal',
        },
        {
          system: 'Coolant',
          specification: 'RED - OAT extended-life (ELC)',
          dealerSpec: 'Heavy-duty extended-life coolant, 50/50 pre-mix',
          capacity: '3.2 gal',
        },
      ],
      coolantCaution:
        'This machine takes red OAT extended-life coolant. Coolant families must not be mixed — '
        + 'topping off with the wrong type can gel the system and destroy its corrosion protection. '
        + 'If you are not certain what is in it, call us rather than guessing.',
    },
    files: [
      { name: 'Loader cylinder parts receipt', type: 'PDF' },
      { name: 'Latest engine-oil analysis', type: 'PDF' },
      { name: 'Fluid specification sheet', type: 'PDF' },
    ],
    ownerGreases: { count: 3, since: 'July 28, 2026' },
    greaseStops: [
      { date: 'July 28, 2026', reading: '620.0 hours' },
      { date: 'May 30, 2026', reading: '548.6 hours' },
      { date: 'December 12, 2025', reading: '402.7 hours' },
      { date: 'September 4, 2025', reading: '321.5 hours' },
    ],
    history: [
      {
        date: 'July 28, 2026',
        reading: '620.0 hours',
        category: 'Maintenance',
        work: '50-hour inspection and chassis service',
        notes: 'Greased all service points, inspected fluid levels, and checked loader pins and bushings.',
      },
      {
        date: 'May 30, 2026',
        reading: '548.6 hours',
        category: 'Maintenance',
        work: 'Engine oil, oil-filter, and fuel-filter service',
        notes: 'Changed fluids and filters, inspected belts, and recorded a clean engine compartment inspection.',
      },
      {
        date: 'February 19, 2026',
        reading: '488.2 hours',
        category: 'Repair',
        work: 'Left loader lift-cylinder repair',
        notes: 'Repacked the cylinder, inspected the rod, and verified operation under load.',
      },
      {
        date: 'December 12, 2025',
        reading: '402.7 hours',
        category: 'Maintenance',
        work: 'Hydraulic filter and suction-screen service',
        notes: 'Replaced the hydraulic filter, cleaned the suction screen, and inspected the drained fluid.',
      },
      {
        date: 'September 4, 2025',
        reading: '321.5 hours',
        category: 'Inspection',
        work: 'Grease run and condition inspection',
        notes: 'Serviced all grease points and documented normal wear at the loader pivot points.',
      },
    ],
  },
  {
    lookupNumber: '0002',
    publicLabel: 'Case 580 Super M backhoe',
    greeting: 'Hey, Jeff!',
    type: 'Example backhoe loader',
    make: 'Case',
    model: '580 Super M',
    programStatus: 'Active',
    reading: { label: 'Current hours', value: '4,218.6 hours', hours: 4218.6, unit: 'hours' },
    verifiedReading: { hours: 4175.0, date: 'July 17, 2026' },
    lastService: {
      title: 'Engine oil and filter service',
      date: 'July 17, 2026',
      reading: '4,175.0 hours',
      detail: 'Changed engine oil and filters, inspected belts, and completed a full grease service.',
    },
    previousRepair: {
      title: 'Boom-return hydraulic hose replacement',
      date: 'April 9, 2026',
      reading: '4,042.3 hours',
      detail: 'Replaced the failed hose, added abrasion sleeve, cleaned the area, and checked for leaks.',
    },
    upcomingService: {
      due: '4,300.0 hours',
      dueAtHours: 4300,
      remaining: '81.4 hours remaining',
      item: 'Replace the hydraulic return filter, inspect boom pins, and grease every service point.',
    },
    checkupSchedule: {
      frequency: 'Weekly checkup schedule',
      nextGreaseDate: 'August 14, 2026',
      basis: 'Scheduled weekly from the July 17 service',
    },
    lastOilChange: { date: 'July 17, 2026', hours: 4175 },
    oilDue: {
      reading: '4,375.0 hours',
      dueAtHours: 4375,
      date: 'October 30, 2026',
      remaining: '156.4 operating hours remaining',
    },
    dataSheet: {
      greaseZerks: '46',
      fluids: [
        {
          system: 'Engine oil',
          specification: 'SAE 15W-40, API CK-4',
          capacity: '14.0 qt',
        },
        {
          system: 'Hydraulic system',
          specification: 'ISO 46 anti-wear (AW-46)',
          dealerSpec: 'Case Akcela AW hydraulic fluid',
          capacity: '24.0 gal',
        },
        {
          system: 'Transmission',
          specification: 'Powershift transmission fluid',
          capacity: '4.5 gal',
        },
        {
          system: 'Coolant',
          specification: 'GREEN - conventional',
          capacity: '5.5 gal',
        },
      ],
      coolantCaution:
        'This machine takes conventional green coolant. Coolant families must not be mixed — '
        + 'topping off with the wrong type can gel the system and destroy its corrosion protection. '
        + 'Note that the other machines on this account do not all take the same type, so check the '
        + 'tag before you pour.',
    },
    files: [
      { name: 'Hydraulic hose parts receipt', type: 'PDF' },
      { name: 'July service checklist', type: 'PDF' },
      { name: 'Fluid specification sheet', type: 'PDF' },
    ],
    ownerGreases: { count: 6, since: 'July 17, 2026' },
    greaseStops: [
      { date: 'July 17, 2026', reading: '4,175.0 hours' },
      { date: 'July 10, 2026', reading: '4,150.2 hours' },
      { date: 'May 23, 2026', reading: '4,108.0 hours' },
      { date: 'January 18, 2026', reading: '3,925.0 hours' },
    ],
    history: [
      {
        date: 'July 17, 2026',
        reading: '4,175.0 hours',
        category: 'Maintenance',
        work: 'Engine oil and filter service',
        notes: 'Changed engine oil and filters, inspected belts, and completed a full grease service.',
      },
      {
        date: 'July 10, 2026',
        reading: '4,150.2 hours',
        category: 'Inspection',
        work: 'Weekly grease run and leak inspection',
        notes: 'Greased all points, checked hydraulic cylinders and hoses, and recorded no active leaks.',
      },
      {
        date: 'May 23, 2026',
        reading: '4,108.0 hours',
        category: 'Inspection',
        work: 'Stabilizer and boom-pin inspection',
        notes: 'Measured pin movement, checked retaining hardware, and documented wear for continued monitoring.',
      },
      {
        date: 'April 9, 2026',
        reading: '4,042.3 hours',
        category: 'Repair',
        work: 'Boom-return hydraulic hose replacement',
        notes: 'Replaced the failed hose, added abrasion sleeve, cleaned the area, and checked for leaks.',
      },
      {
        date: 'January 18, 2026',
        reading: '3,925.0 hours',
        category: 'Maintenance',
        work: 'Scheduled 500-hour service',
        notes: 'Changed engine and hydraulic filters, checked axle fluids, and inspected the cooling system.',
      },
    ],
  },
  {
    lookupNumber: '0003',
    publicLabel: 'Ford F-350 service truck',
    greeting: 'Hey, Jeff!',
    type: 'Example service truck',
    make: 'Ford',
    model: 'F-350',
    programStatus: 'Active',
    // Mileage rather than hours. The self-report control reads this and relabels itself -
    // "I just greased the machine" and "hours on the meter" are wrong for a truck.
    reading: { label: 'Current mileage', value: '128,640 miles', hours: 128640, unit: 'miles' },
    verifiedReading: { hours: 127200, date: 'July 24, 2026' },
    lastService: {
      title: 'Engine oil, fuel-filter, and tire service',
      date: 'July 24, 2026',
      reading: '127,200 miles',
      detail: 'Changed engine oil and filters, rotated the tires, and inspected steering and suspension.',
    },
    previousRepair: {
      title: 'Upper and lower ball-joint replacement',
      date: 'May 2, 2026',
      reading: '123,850 miles',
      detail: 'Replaced the front ball joints, inspected the wheel bearings, and completed an alignment.',
    },
    upcomingService: {
      due: '130,000 miles',
      dueAtHours: 130000,
      remaining: '1,360 miles remaining',
      item: 'Replace both fuel filters and service the front locking hubs.',
    },
    checkupSchedule: {
      frequency: 'Monthly checkup schedule',
      nextGreaseDate: 'August 24, 2026',
      basis: 'Scheduled monthly from the July 24 service',
    },
    // 3,000-mile oil change (Daniel 2026-08-14): the mileage counterpart of the 250-hour
    // standard for hour-based equipment. Counted from the last verified oil change - the
    // July 24 service at 127,200 - so the next one lands at 130,200. The same 12-month
    // expiry applies, and on a truck covering ~1,400 miles a month the mileage trips first,
    // which is exactly what "whichever comes first" is there to catch.
    lastOilChange: { date: 'July 24, 2026', hours: 127200, unit: 'miles' },
    oilDue: {
      reading: '130,200 miles',
      dueAtHours: 130200,
      date: 'July 24, 2027',
      remaining: '1,560 miles remaining',
    },
    dataSheet: {
      greaseZerks: '8 serviceable points',
      fluids: [
        {
          system: 'Engine oil',
          specification: 'SAE 10W-30, API CK-4',
          capacity: '13.0 qt',
        },
        {
          system: 'Coolant',
          specification: 'Extended-life OAT',
          dealerSpec: 'Motorcraft orange, 50/50 pre-mix',
          capacity: '7.5 gal',
        },
        {
          system: 'Transfer case',
          specification: 'Low-viscosity automatic transmission fluid',
          capacity: '1.6 qt',
        },
      ],
      coolantCaution:
        'This truck takes an extended-life OAT coolant. Coolant families must not be mixed — '
        + 'topping off with the wrong type can gel the system and destroy its corrosion protection. '
        + 'If a machine holds a proprietary colour, either stay with that product or flush completely '
        + 'before converting.',
    },
    files: [
      { name: 'Ball-joint parts invoice', type: 'PDF' },
      { name: 'Alignment printout', type: 'PDF' },
      { name: 'July service receipt', type: 'PDF' },
    ],
    ownerGreases: { count: 1, since: 'July 24, 2026' },
    greaseStops: [
      { date: 'July 24, 2026', reading: '127,200 miles' },
      { date: 'February 14, 2026', reading: '120,430 miles' },
      { date: 'August 18, 2025', reading: '110,040 miles' },
    ],
    history: [
      {
        date: 'July 24, 2026',
        reading: '127,200 miles',
        category: 'Maintenance',
        work: 'Engine oil, fuel-filter, and tire service',
        notes: 'Changed engine oil and filters, rotated the tires, and inspected steering and suspension.',
      },
      {
        date: 'May 2, 2026',
        reading: '123,850 miles',
        category: 'Repair',
        work: 'Upper and lower ball-joint replacement',
        notes: 'Replaced the front ball joints, inspected the wheel bearings, and completed an alignment.',
      },
      {
        date: 'February 14, 2026',
        reading: '120,430 miles',
        category: 'Maintenance',
        work: 'Engine oil and fuel-filter service',
        notes: 'Changed engine oil, replaced both fuel filters, and inspected the charge-air piping.',
      },
      {
        date: 'November 7, 2025',
        reading: '115,910 miles',
        category: 'Repair',
        work: 'Front brake service',
        notes: 'Replaced front pads and rotors, serviced caliper slides, and flushed the brake fluid.',
      },
      {
        date: 'August 18, 2025',
        reading: '110,040 miles',
        category: 'Maintenance',
        work: 'Transmission and driveline service',
        notes: 'Changed transmission fluid and filter, checked axle levels, and lubricated the driveline.',
      },
    ],
  },
];
